import { configureStore } from "@reduxjs/toolkit";
import rootReducer from "./rootReducer";
import createSagaMiddleware from "redux-saga";
import SagaData from "./saga";

const sagaMiddleware = createSagaMiddleware();

// Persist ONLY the session slice. The other slices (threads.liveMessages holds
// full email HTML bodies pushed over SSE; notifications; content) are runtime
// caches that grow without bound — persisting the whole store used to blow the
// ~5MB localStorage quota, after which saveState's silent catch FROZE the
// stored snapshot with an old token: every reload rehydrated an expired token
// and logged the user out, and re-logins couldn't persist either. Reload only
// ever needs the session; everything else refetches from the server.
const pickPersisted = (state) => ({ reducer: state?.reducer });

const loadState = () => {
  try {
    const serializedState = localStorage.getItem("state");
    if (serializedState === null) {
      return undefined;
    }
    // Pick, don't spread: a legacy pre-fix snapshot may still carry megabytes
    // of thread HTML — rehydrating it would re-poison every future save.
    return pickPersisted(JSON.parse(serializedState));
  } catch (err) {
    return undefined;
  }
};

// SECURITY (deferred): this persists the session — including the JWT — to
// localStorage, which is readable by any script running in this origin. A
// stored-XSS payload could therefore exfiltrate the token. The proper fix is to
// move the token to an httpOnly cookie, but that is a cross-cutting backend
// change and is out of scope here. We must NOT stop persisting the token: auth
// reads it back from localStorage on reload, so dropping it would break login.
// Compensating control: all untrusted email/quote HTML is sanitized with
// DOMPurify at every dangerouslySetInnerHTML sink (see Helper/sanitizeHtml.js),
// and doc-preview iframes are sandboxed, so the XSS entry point is closed.
const saveState = (state) => {
  try {
    localStorage.setItem("state", JSON.stringify(pickPersisted(state)));
  } catch {
    // A failed write must NEVER cost the session (that was the reload-logout
    // bug): fall back to the bare minimum needed to stay signed in.
    try {
      localStorage.setItem(
        "state",
        JSON.stringify({
          reducer: {
            userToken: state?.reducer?.userToken || null,
            userLoggedIn: state?.reducer?.userLoggedIn || null,
          },
        })
      );
    } catch {
      // Storage genuinely unavailable (private mode / disabled) — nothing to do.
    }
  }
};

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(sagaMiddleware),
  preloadedState: loadState(),
});

store.subscribe(() => {
  saveState(store.getState());
});

sagaMiddleware.run(SagaData);
export default store;
